import {toast} from "react-toastify";

import type {Dispatch} from "redux";

import {ToastContent} from "components";
import type {TRootState} from "types";

import mapXMarketMapsApi from "api/marketMapApi";
import {getMemoryToken} from "api";

import {clearCancelToken, getCancelToken} from "api/cancelTokens";
import {TPaginationParam} from "api/candidateApi/types";
import {TCreateMarketMap} from "api/marketMapApi/types";
import {GET_MARKET_MAPS_TOKEN} from "api/requestCancelTokenStrings";
import axios, {AxiosResponse} from "axios";
import {getItem} from "helpers/localStorage";
import {successResponse} from "helpers/map";
import {
	addToMarketMapModal,
	createMarketMapDone,
	createMarketMapFail,
	createMarketMapInit,
	deleteMarketMapDone,
	deleteMarketMapFail,
	deleteMarketMapInit,
	downloadMarketMapsPngDone,
	downloadMarketMapsPngFail,
	downloadMarketMapsPngInit,
	getMarketMapsDone,
	getMarketMapsFail,
	getMarketMapsForModalDone,
	getMarketMapsForModalFail,
	getMarketMapsForModalInit,
	getMarketMapsInit,
	refreshMarketMapDone,
	updateMarketMapDone,
	updateMarketMapFail,
	updateMarketMapInit,
} from "store/market-map/marketMapActions";
import {
	marketMapModalNameSelector,
	marketMapsModalPaginationSelector,
	marketMapsPaginationSelector,
} from "store/market-map/marketMapSelectors";
import {globalPerPageSelector} from "store/userPreference/userPreferenceSelectors";

export const getMarketMaps =
	(
		name = "",
		createdBy: ("self" | "organisation" | "mapx")[] = [],
		pagination?: TPaginationParam,
	) =>
	async (dispatch: Dispatch, getState: () => TRootState) => {
		try {
			dispatch(getMarketMapsInit());

			const state = getState();

			const currentPagination = marketMapsPaginationSelector(state);

			const globalPerPageValue = globalPerPageSelector(state);

			const finalPagination = {
				page: pagination?.page ?? currentPagination?.page ?? 1,
				per_page: globalPerPageValue ?? pagination?.per_page ?? 20,
			};

			const payload = {
				page: finalPagination.page,
				per_page: finalPagination.per_page,
				name,
				created_by: createdBy,
			};

			// Remove falsy values
			Object.keys(payload).forEach((k) => {
				const key = k as keyof typeof payload;
				if (!payload[key]) delete payload[key];
				if (
					Array.isArray(payload[key]) &&
					(payload[key] as ("self" | "organisation" | "mapx")[]).length === 0
				) {
					delete payload[key];
				}
			});

			const response = await mapXMarketMapsApi.getMarketMaps(payload, {
				cancelToken: getCancelToken(GET_MARKET_MAPS_TOKEN).token,
			});

			if (response && successResponse(response, 200)) {
				const data = response.data;
				dispatch(getMarketMapsDone(data, name, createdBy));
			}
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled", e.message);
			} else {
				dispatch(getMarketMapsFail(e));
			}
		} finally {
			clearCancelToken(GET_MARKET_MAPS_TOKEN);
		}
	};

export const getMarketMapsForModal =
	(name = "") =>
	async (dispatch: Dispatch, getState: () => TRootState) => {
		try {
			const state = getState();
			const existingName = marketMapModalNameSelector(state);

			const nameChanged = name !== existingName;

			const pagination = nameChanged
				? {page: 0, pages: 1}
				: marketMapsModalPaginationSelector(state);

			if (pagination.pages <= pagination.page) {
				// dispatch(getMarketMapsForModalDone({pagination, results: []}, name));

				return;
			}

			dispatch(getMarketMapsForModalInit());

			const payload = {
				page: ((pagination?.page || 0) as number) + 1,
				name,
			};

			// Remove falsy values
			Object.keys(payload).forEach((k) => {
				const key = k as keyof typeof payload;
				if (!payload[key]) delete payload[key];
			});

			const {data} = await mapXMarketMapsApi.getMarketMaps(payload);

			dispatch(getMarketMapsForModalDone(data, name));
		} catch (e) {
			dispatch(getMarketMapsForModalFail(e));
		}
	};

export const updateMarketMap =
	(id: number, name: string, isGlobal: boolean, companyIds: number[], description?: string) =>
	async (dispatch: Dispatch) => {
		try {
			dispatch(updateMarketMapInit());

			const payload: TCreateMarketMap = {
				company_ids: companyIds,
				description: description,
				is_global: isGlobal,
				name,
			};

			const resp: AxiosResponse = await mapXMarketMapsApi.updateMarketMap(id, payload);

			if (resp?.status === 200) {
				dispatch(updateMarketMapDone(resp.data));
				dispatch(addToMarketMapModal(false));
			}
			if (resp?.status === 409) {
				dispatch(
					updateMarketMapFail({
						message: "Such name already exists. Try another?",
						status: 409,
					}),
				);
			}
		} catch (e) {
			dispatch(updateMarketMapFail(e));
		}
	};

export const createMarketMap =
	(name: string, isGlobal: boolean, companyIds: number[], description?: string) =>
	async (dispatch: Dispatch) => {
		try {
			dispatch(createMarketMapInit());

			const payload = {
				company_ids: companyIds,
				description,
				is_global: isGlobal,
				name,
			};

			if (!description) {
				delete payload.description;
			}

			const resp: AxiosResponse = await mapXMarketMapsApi.createMarketMap(payload);

			if (resp?.status === 201) {
				dispatch(createMarketMapDone(resp.data));
			}
			if (resp?.status === 409) {
				dispatch(
					createMarketMapFail({
						message: "Such name already exists. Try another?",
						status: 409,
					}),
				);
			}
		} catch (e) {
			dispatch(createMarketMapFail(e));
		}
	};

export const deleteMarketMap = (id: number) => async (dispatch: Dispatch) => {
	try {
		dispatch(deleteMarketMapInit(id));

		await mapXMarketMapsApi.deleteMarketMap(id);

		dispatch(deleteMarketMapDone(id));
	} catch (e) {
		dispatch(deleteMarketMapFail(id, e));
	}
};

export const refreshMarketMapById = (id: number) => async (dispatch: Dispatch) => {
	try {
		const response: AxiosResponse = await mapXMarketMapsApi.getMarketMap(id);

		if (successResponse(response, 200)) {
			dispatch(refreshMarketMapDone(response.data));
		}
	} catch (e) {
		console.error("Error refreshing market map:", e);
	}
};

export const downloadMarketMapsPng = (id: number, name: string) => async (dispatch: Dispatch) => {
	try {
		dispatch(downloadMarketMapsPngInit());

		const savedToken = getMemoryToken();
		const expiredTime = getItem("expires_in")
			? parseInt(getItem("expires_in") as string, 10)
			: null;

		if (expiredTime && expiredTime <= new Date().getTime()) {
			dispatch(downloadMarketMapsPngFail("Error downloading image: Token is expired"));

			toast.error(ToastContent, {
				data: {title: "Token is expired", description: "Refresh the page and try again."},
			});

			return;
		}

		const isDev = process.env.NODE_ENV === "development";
		const url = new URL(window.location.href);
		if (isDev) {
			url.port = "8080";
		}
		url.pathname = "/png";
		url.searchParams.append(
			"url",
			`${isDev ? "http://" : "https://"}${
				window.location.host
			}/market-maps/${id}/${savedToken}`,
		);
		url.searchParams.append("elementId", "market-map-png");

		const res = await axios.get(url.toString(), {
			responseType: "arraybuffer",
		});

		if (res.status === 200) {
			const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.setAttribute("download", `${name}.png`);
			document.body.appendChild(link);
			link.click();

			dispatch(downloadMarketMapsPngDone());
		} else {
			dispatch(downloadMarketMapsPngFail(`Error downloading image: ${res.status}`));

			toast.error(ToastContent, {data: {title: "Cannot download the image"}});
		}
	} catch (e) {
		dispatch(downloadMarketMapsPngFail(e));

		toast.error(ToastContent, {data: {title: "Cannot download the image"}});
	}
};
